import Typography from '@mui/material/Typography';
import { Alert, Divider, Grid } from '@mui/material';
import React from 'react';
import uuid from 'react-uuid';
import Button from '@mui/material/Button';
import { AccountData, BuildingBlockType, EmailBlockData, ReviewsConfigData } from '../../../api';
import { useAuth } from '../../../session/InternalAuthProvider';
import { REVIEW_PLATFORM_ICONS } from './EditReviewsDialog';

type RenderProps = {
  t?: any;
  isSmallScreen?: boolean;
  gridDirection?: 'row' | 'row-reverse';
  borderColor?: string;
  linkColor?: string;
};

export interface DraggableItem {
  id: string;
  type: BuildingBlockType;
  render: (renderProps: RenderProps) => JSX.Element;
  toEmailBlockData?: () => EmailBlockData;
  // for items that are should not be moveable
  isStatic?: boolean;
  hasError?: () => boolean;
}

export class TextItem implements DraggableItem {
  id: string;

  type = BuildingBlockType.TEXT;

  content: string;

  constructor(id: string, content: string) {
    this.id = id;
    this.content = content;
  }

  toEmailBlockData = (): EmailBlockData => ({
    type: BuildingBlockType.TEXT,
    content: this.content,
  });

  render = ({ gridDirection, linkColor }: RenderProps) => (
    <Grid container flexDirection={gridDirection}>
      <div
        dangerouslySetInnerHTML={{
          __html: this.content.replaceAll(
            /<a /g,
            `<a style="color: ${linkColor || '#4abd00'}; text-decoration: underline;" `,
          ),
        }}
      />
    </Grid>
  );
}

export class LineBreakItem implements DraggableItem {
  id: string;

  type = BuildingBlockType.LINE_BREAK;

  constructor(id: string) {
    this.id = id;
  }

  render = ({ borderColor }: RenderProps) => (
    <Grid width="80%">
      <Divider style={{ width: '100%', border: `1px solid ${borderColor || '#4fbf13'} ` }} />
    </Grid>
  );

  toEmailBlockData = (): EmailBlockData => ({
    type: BuildingBlockType.LINE_BREAK,
  });
}

export class ImageItem implements DraggableItem {
  id: string;

  type = BuildingBlockType.IMAGE;

  src: string;

  constructor(id: string, src: string) {
    this.id = id;
    this.src = src;
  }

  render = ({ t, isSmallScreen }: RenderProps) =>
    this.src === '' ? (
      <Typography>{t('emailTemplates.noImage')}</Typography>
    ) : (
      <Grid container direction="column" flexWrap="nowrap" maxWidth="80%">
        <img
          src={this.src.replace('raw', 'image').concat('?w=600')}
          width={isSmallScreen ? '120px' : undefined}
          alt="logo"
        />
      </Grid>
    );

  toEmailBlockData = (): EmailBlockData => ({
    type: BuildingBlockType.IMAGE,
    src: this.src,
  });
}

export class ReturningCustomerCodeItem implements DraggableItem {
  id: string;

  type = BuildingBlockType.RETURNING_CUSTOMER;

  constructor(id: string) {
    this.id = id;
  }

  render = ({ borderColor, t }: RenderProps) => {
    return (
      <Grid container flexDirection="column" gap={2}>
        {this.hasError() && (
          <Alert severity="warning">
            <Grid container flexDirection="column">
              <Typography>{t('emailTemplates.needsReturningCustomer')}</Typography>
              <Typography>{t('emailTemplates.gotoSettings')}</Typography>
            </Grid>
          </Alert>
        )}
        <Grid
          container
          width="80%"
          justifyContent="center"
          p={2}
          borderRadius="8px"
          style={{ border: `2px solid ${borderColor || '#4fbf13'} ` }}>
          XXXXXXXXXXXXXX
        </Grid>
      </Grid>
    );
  };

  toEmailBlockData = (): EmailBlockData => ({
    type: BuildingBlockType.RETURNING_CUSTOMER,
  });

  hasError = () => {
    const { authState } = useAuth();
    const account = authState.account as unknown as AccountData;
    const hasReturningCustomerFeatureOn = !!account.returning_customer_settings;
    return !hasReturningCustomerFeatureOn;
  };
}

export class ReviewsItem implements DraggableItem {
  id: string;

  type = BuildingBlockType.REVIEWS;

  reviewsConfig?: ReviewsConfigData;

  constructor(id: string, reviewsConfig?: ReviewsConfigData) {
    this.id = id;
    this.reviewsConfig = reviewsConfig;
  }

  render = ({ borderColor, t }: RenderProps) => {
    return (
      <Grid container flexDirection="column" gap={2}>
        <Grid container spacing={2} width="80%">
          {Object.entries(this.reviewsConfig || {})
            .filter(([_, config]) => config?.enabled)
            .map(([platform]) => (
              <Grid item xs={4} key={platform}>
                <Grid
                  container
                  height="160px"
                  borderRadius="8px"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="space-between"
                  my={2}
                  style={{ border: `1px solid ${borderColor || '#4fbf13'}` }}>
                  <Grid item mt={1}>
                    <img
                      src={REVIEW_PLATFORM_ICONS[platform as keyof typeof REVIEW_PLATFORM_ICONS]}
                      alt={t(`emailTemplates.${platform}`)}
                      style={{ width: 48, height: 48 }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" align="center">
                      {t(`emailTemplates.${platform}`)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button>
                      <Typography
                        style={{
                          backgroundColor: borderColor || '#4fbf13',
                          color: '#ffffff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}>
                        {t('emailTemplates.review')}
                      </Typography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Grid>
    );
  };

  toEmailBlockData = (): EmailBlockData => ({
    type: BuildingBlockType.REVIEWS,
    reviews_config: this.reviewsConfig,
  });

  hasError = () => {
    return false;
  };
}

export function createDraggableItem(data: EmailBlockData): DraggableItem {
  const id = `${data.type}-${uuid()}`;
  switch (data.type) {
    case BuildingBlockType.TEXT:
      return new TextItem(id, data.content || '');
    case BuildingBlockType.LINE_BREAK:
      return new LineBreakItem(id);
    case BuildingBlockType.IMAGE:
      return new ImageItem(id, data.src || '');
    case BuildingBlockType.RETURNING_CUSTOMER:
      return new ReturningCustomerCodeItem(id);
    case BuildingBlockType.REVIEWS:
      return new ReviewsItem(id, data.reviews_config);
    default:
      throw new Error(`Unsupported BuildingBlockType: ${data.type}`);
  }
}
